import path from 'path';
import { child_process, fs, https, os } from '../../lib/cep/node';
import { getTempFolderPath } from './folder.util';

export type LaunchResult = {
  method: 'open' | 'spawn';
  durationMs: number;
};

/** Windows `spawn` reports ENOENT asynchronously; wait this long for it. */
const SPAWN_ERROR_GRACE_MS = 500;

export const openExecutableFile = async (
  filePath: string
): Promise<LaunchResult> => {
  const platform = os.platform();
  const startedAt = Date.now();

  if (platform === 'darwin') {
    return new Promise<LaunchResult>((resolve, reject) => {
      child_process.exec(`open "${filePath}"`, (error, stdout, stderr) => {
        // resolve/reject from inside the callback: resolving outside reported
        // success even when `open` failed, which made "installer launched"
        // untrustworthy.
        if (error) {
          console.error(`exec error: ${error}`);
          reject(error);
          return;
        }
        if (stderr) {
          console.error(`stderr: ${stderr}`);
        }
        if (stdout) {
          console.log(`stdout: ${stdout}`);
        }
        resolve({ method: 'open', durationMs: Date.now() - startedAt });
      });
    });
  }

  if (platform === 'win32') {
    return new Promise<LaunchResult>((resolve, reject) => {
      let settled = false;

      const child = child_process.spawn(filePath, {
        detached: true,
        stdio: 'ignore',
      });

      child.on('error', error => {
        if (settled) return;
        settled = true;
        console.error(`spawn error: ${error}`);
        reject(error);
      });

      // `spawn` cannot be awaited (the installer outlives us), so give it a
      // short window to surface a launch failure before reporting success.
      setTimeout(() => {
        if (settled) return;
        settled = true;
        child.unref();
        resolve({ method: 'spawn', durationMs: Date.now() - startedAt });
      }, SPAWN_ERROR_GRACE_MS);
    });
  }

  throw new Error(`Unsupported platform: ${platform}`);
};

export const getInstallerPath = () => {
  const platform = os.platform();
  const appDataFolder = getTempFolderPath();
  return path.join(
    appDataFolder,
    platform === 'darwin' ? 'AutoCut.dmg' : 'AutoCut-win-Setup.exe'
  );
};

export const doesInstallerFileExist = () => {
  const installerPath = getInstallerPath();
  return fs.existsSync(installerPath);
};

const MAX_REDIRECTS = 5;

type OnProgress = (percent: number) => void;

export type DownloadResult = {
  filePath: string;
  requestUrl: string;
  statusCode: number;
  totalBytes: number;
  downloadedBytes: number;
  redirects: number;
  durationMs: number;
};

const fetchToFile = (
  requestUrl: string,
  destinationPath: string,
  redirectsLeft: number,
  startedAt: number,
  onProgress?: OnProgress
): Promise<DownloadResult> => {
  return new Promise<DownloadResult>((resolve, reject) => {
    const request = https.get(requestUrl, response => {
      const statusCode = response.statusCode ?? 0;

      const isRedirect = statusCode >= 300 && statusCode < 400;
      if (isRedirect) {
        const location = response.headers.location;
        response.resume();
        if (!location) {
          reject(
            new Error(
              `Redirect from ${requestUrl} missing Location header (status ${statusCode})`
            )
          );
          return;
        }
        if (redirectsLeft <= 0) {
          reject(new Error(`Too many redirects while downloading installer`));
          return;
        }
        const nextUrl = new URL(location, requestUrl).toString();
        resolve(
          fetchToFile(
            nextUrl,
            destinationPath,
            redirectsLeft - 1,
            startedAt,
            onProgress
          )
        );
        return;
      }

      if (statusCode !== 200) {
        response.resume();
        reject(
          new Error(
            `Failed to download installer from ${requestUrl} (status ${statusCode})`
          )
        );
        return;
      }

      const fileStream = fs.createWriteStream(destinationPath);
      const totalBytes = Number(response.headers['content-length'] ?? 0);
      let downloadedBytes = 0;
      let lastPercent = -1;

      const handleFailure = (error: Error) => {
        fileStream.destroy();
        fs.unlink(destinationPath, () => reject(error));
      };

      response.on('data', (chunk: Buffer) => {
        downloadedBytes += chunk.length;
        if (totalBytes <= 0) {
          return;
        }
        const percent = Math.min(
          100,
          Math.floor((downloadedBytes / totalBytes) * 100)
        );
        if (percent === lastPercent) {
          return;
        }
        lastPercent = percent;
        onProgress?.(percent);
      });

      response.on('error', handleFailure);
      fileStream.on('error', handleFailure);
      fileStream.on('finish', () => {
        onProgress?.(100);
        resolve({
          filePath: destinationPath,
          requestUrl,
          statusCode,
          totalBytes,
          downloadedBytes,
          redirects: MAX_REDIRECTS - redirectsLeft,
          durationMs: Date.now() - startedAt,
        });
      });

      response.pipe(fileStream);
    });

    request.on('error', reject);
  });
};

export const getInstallerUrl = () =>
  os.platform() === 'darwin'
    ? 'https://releases.static.autocut.com/AutoCut.dmg'
    : 'https://releases.static.autocut.com/AutoCut-win-Setup.exe';

export const downloadInstaller = async (
  onProgress?: OnProgress
): Promise<DownloadResult> => {
  const installerUrl = getInstallerUrl();

  const installerPath = getInstallerPath();
  if (fs.existsSync(installerPath)) {
    // `unlinkSync` rather than `rmSync`: CEP ships an old Node and `rmSync`
    // only exists from 14.14.
    fs.unlinkSync(installerPath);
  }

  return fetchToFile(
    installerUrl,
    installerPath,
    MAX_REDIRECTS,
    Date.now(),
    onProgress
  );
};
